import { useState, useCallback, useMemo } from "react"
import { format, parse, isValid, startOfWeek, endOfWeek } from "date-fns"
import * as React from "react"
import type { DateRange } from "react-day-picker"
import type { RendererProps } from "../core/element-registry"
import { useRenderContext } from "../core/render-context"
import { useAtomDefaults } from "../contexts/atom-defaults-context"
import { useBoundValue } from "../hooks/use-bound-value"
import { Calendar } from "../components/ui/calendar"
import { cn } from "../lib/utils"

type CalendarMode = "single" | "range" | "week"

/* ------------------------------------------------------------------ */
/*  Overlay helpers                                                   */
/* ------------------------------------------------------------------ */

/** Format a number for compact display (e.g., 50000 -> "50K") */
function formatOverlayNumber(value: number): string {
  if (value === 0) return ""
  if (Math.abs(value) >= 1_000_000) {
    return `${(value / 1_000_000).toFixed(value % 1_000_000 === 0 ? 0 : 1)}M`
  }
  if (Math.abs(value) >= 1_000) {
    return `${(value / 1_000).toFixed(value % 1_000 === 0 ? 0 : 1)}K`
  }
  return String(value)
}

/** Check whether an overlay entry has any non-zero numeric values (excluding 'date' key) */
function hasNonZeroValues(entry: Record<string, unknown>): boolean {
  for (const [key, val] of Object.entries(entry)) {
    if (key === "date") continue
    if (typeof val === "number" && val !== 0) return true
  }
  return false
}

/** Build a date-keyed map from an overlay data array for O(1) lookup */
function buildOverlayMap(
  data: unknown,
): Map<string, Record<string, unknown>> {
  const map = new Map<string, Record<string, unknown>>()
  if (!Array.isArray(data)) return map
  for (const item of data) {
    if (item && typeof item === "object" && typeof item.date === "string") {
      if (hasNonZeroValues(item as Record<string, unknown>)) {
        map.set(item.date, item as Record<string, unknown>)
      }
    }
  }
  return map
}

/* ------------------------------------------------------------------ */
/*  Date utilities                                                    */
/* ------------------------------------------------------------------ */

/**
 * Parse a "YYYY-MM" string into a Date for the 1st of that month.
 * Returns undefined if the input is not a valid "YYYY-MM" format.
 */
function parseYearMonth(raw: unknown): Date | undefined {
  if (typeof raw !== "string" || !/^\d{4}-\d{2}$/.test(raw)) return undefined
  const d = parse(raw, "yyyy-MM", new Date())
  return isValid(d) ? d : undefined
}

/**
 * Coerce a bound value string into a Date for single mode.
 */
function coerceSingleDate(raw: unknown): Date | undefined {
  if (!raw || typeof raw !== "string") return undefined
  const d = parse(raw, "yyyy-MM-dd", new Date())
  return isValid(d) ? d : undefined
}

/* ------------------------------------------------------------------ */
/*  Custom Day component for overlay rendering                        */
/* ------------------------------------------------------------------ */

interface OverlayDayProps {
  day: { date: Date }
  modifiers: Record<string, boolean>
  children?: React.ReactNode
  [key: string]: unknown
}

function createOverlayDay(
  overlayMap: Map<string, Record<string, unknown>>,
) {
  return function OverlayDay(props: OverlayDayProps) {
    const { day, modifiers, ...tdProps } = props
    const dateStr = format(day.date, "yyyy-MM-dd")
    const entry = overlayMap.get(dateStr)

    if (!entry) {
      return <td {...tdProps} />
    }

    // Extract non-date numeric values for display
    const values: Array<{ key: string; value: number; formatted: string }> = []
    for (const [key, val] of Object.entries(entry)) {
      if (key === "date") continue
      if (typeof val === "number" && val !== 0) {
        values.push({ key, value: val, formatted: formatOverlayNumber(val) })
      }
    }

    if (values.length === 0) {
      return <td {...tdProps} />
    }

    // Inject data-overlay attribute and overlay elements
    const { children, ...restTd } = tdProps as Record<string, unknown> & { children?: React.ReactNode }
    return (
      <td {...restTd} data-overlay="true">
        {children}
        <div
          style={{
            fontSize: "0.6rem",
            lineHeight: 1.1,
            textAlign: "center",
            marginTop: "-2px",
            pointerEvents: "none",
          }}
        >
          {values.slice(0, 2).map((v) => (
            <div key={v.key} style={{ opacity: 0.7 }}>
              {v.formatted}
            </div>
          ))}
        </div>
      </td>
    )
  }
}

/* ------------------------------------------------------------------ */
/*  CalendarRenderer                                                  */
/* ------------------------------------------------------------------ */

export function CalendarRenderer({ element }: RendererProps) {
  const { resolveSymbol, dispatchEvent } = useRenderContext()
  // Theme-v2 atom defaults feed the fallback chain:
  //   element.variant/size → atomDefaults.default* → shadcn "default"
  const atomDefaults = useAtomDefaults("calendar")
  const resolvedVariant = (element.variant as string | undefined)
    ?? atomDefaults.defaultVariant
    ?? "default"
  const resolvedSize = (element as { size?: string }).size
    ?? atomDefaults.defaultSize
    ?? "default"
  const calendarDataAttrs = {
    "data-atom-type": "calendar" as const,
    "data-variant": resolvedVariant,
    "data-size": resolvedSize,
  }
  const mode: CalendarMode =
    (element.options?.mode as CalendarMode) || "single"

  // ---------- Primary bind value (single mode) ----------
  const [boundDate, setBoundDate] = useBoundValue<Date | undefined>(
    mode === "single" ? element.bind : undefined,
    resolveSymbol,
    undefined,
    coerceSingleDate,
  )

  // ---------- Range state (range & week modes) ----------
  const [rangeValue, setRangeValue] = useState<DateRange | undefined>(undefined)

  // ---------- Month-picker integration ----------
  const monthBind = element.options?.monthBind as string | undefined
  const monthRaw = monthBind
    ? resolveSymbol(
        monthBind.startsWith("#{") ? monthBind : `#{${monthBind}}`,
      )
    : undefined
  const boundMonth = parseYearMonth(monthRaw)
  const [localMonth, setLocalMonth] = useState<Date | undefined>(boundMonth)

  // Controlled month: prefer boundMonth (external), fall back to localMonth
  const displayMonth = boundMonth ?? localMonth

  // ---------- Overlay data ----------
  const overlayDataPath = element.options?.overlayData as string | undefined
  const overlayRaw = overlayDataPath
    ? resolveSymbol(
        overlayDataPath.startsWith("#{")
          ? overlayDataPath
          : `#{${overlayDataPath}}`,
      )
    : undefined
  const overlayMap = useMemo(() => buildOverlayMap(overlayRaw), [overlayRaw])
  const OverlayDayComponent = useMemo(
    () => (overlayMap.size > 0 ? createOverlayDay(overlayMap) : undefined),
    [overlayMap],
  )

  const handleMonthChange = useCallback(
    (month: Date) => {
      setLocalMonth(month)
      if (monthBind) {
        const yearMonthStr = `${month.getFullYear()}${String(month.getMonth() + 1).padStart(2, "0")}`
        const monthNames = [
          "January", "February", "March", "April", "May", "June",
          "July", "August", "September", "October", "November", "December",
        ]
        const yearMonthLabel = `${monthNames[month.getMonth()]} ${month.getFullYear()}`
        dispatchEvent(element.id, "monthChange", {
          year: month.getFullYear(),
          month: month.getMonth() + 1,
          value: format(month, "yyyy-MM"),
          yearMonthStr,
          yearMonthLabel,
        })
      }
    },
    [monthBind, element.id, dispatchEvent],
  )

  // ---------- Selection handlers per mode ----------

  const handleSingleSelect = useCallback(
    (date: Date | undefined) => {
      setBoundDate(date)
      const value = date ? format(date, "yyyy-MM-dd") : ""
      dispatchEvent(element.id, "change", { value })
    },
    [element.id, dispatchEvent, setBoundDate],
  )

  const handleRangeSelect = useCallback(
    (range: DateRange | undefined) => {
      setRangeValue(range)
      if (range?.from && range?.to) {
        const from = format(range.from, "yyyy-MM-dd")
        const to = format(range.to, "yyyy-MM-dd")
        dispatchEvent(element.id, "change", {
          from,
          to,
          value: `${from}~${to}`,
        })
      } else if (range?.from) {
        // Partial selection (only start picked so far) -- dispatch partial
        const from = format(range.from, "yyyy-MM-dd")
        dispatchEvent(element.id, "change", {
          from,
          to: "",
          value: from,
        })
      }
    },
    [element.id, dispatchEvent],
  )

  const handleWeekSelect = useCallback(
    (range: DateRange | undefined) => {
      if (!range?.from) return
      // Compute Mon-Sun week from the clicked day
      const weekStart = startOfWeek(range.from, { weekStartsOn: 1 })
      const weekEnd = endOfWeek(range.from, { weekStartsOn: 1 })
      const fullWeek: DateRange = { from: weekStart, to: weekEnd }
      setRangeValue(fullWeek)

      const from = format(weekStart, "yyyy-MM-dd")
      const to = format(weekEnd, "yyyy-MM-dd")
      dispatchEvent(element.id, "change", {
        from,
        to,
        value: `${from}~${to}`,
      })
    },
    [element.id, dispatchEvent],
  )

  // ---------- Render ----------

  const monthProps = displayMonth
    ? { month: displayMonth, onMonthChange: handleMonthChange }
    : { onMonthChange: handleMonthChange }

  // Overlay components override (only if overlay data exists)
  const overlayComponents = OverlayDayComponent
    ? { Day: OverlayDayComponent as any }
    : undefined

  if (mode === "range" || mode === "week") {
    return (
      <div
        className={cn("inline-block", element.className)}
        data-testid={`calendar-${element.id}`}
        {...calendarDataAttrs}
      >
        <Calendar
          mode="range"
          selected={rangeValue}
          onSelect={mode === "week" ? handleWeekSelect : handleRangeSelect}
          components={overlayComponents}
          {...monthProps}
        />
      </div>
    )
  }

  // Default: single mode
  return (
    <div
      className={cn("inline-block", element.className)}
      data-testid={`calendar-${element.id}`}
      {...calendarDataAttrs}
    >
      <Calendar
        mode="single"
        selected={boundDate}
        onSelect={handleSingleSelect}
        components={overlayComponents}
        {...monthProps}
      />
    </div>
  )
}
